<template>
    <div class="EnumOptionsInput">
        <label class="EnumOptionsInput__label">
            Options <span class="EnumOptionsInput__required" aria-hidden="true">*</span>
        </label>

        <!--
            PRODUCT-26110 Task 8b Step 3: vuedraggable 2 -> 4. v4 requires an `item`
            scoped-slot template instead of a plain default slot with a caller-owned
            v-for, plus a mandatory `item-key` (each item's `key` field is already a
            stable unique id, previously used as the v-for `:key`). `:model-value`/
            `@update:model-value` replace `v-model` -- explicit binding sidesteps the
            MODE-2 (COMPONENT_V_MODEL not yet flipped) old-style `value`/`input`
            v-model compilation that would otherwise target props v4 no longer has.
        -->
        <draggable
            :model-value="items"
            tag="div"
            item-key="key"
            class="EnumOptionsInput__list"
            handle=".EnumOptionsInput__dragHandle"
            ghost-class="EnumOptionsInput__row--ghost"
            :disabled="disabled || reorderSaving"
            @update:model-value="items = $event"
            @end="onDragEnd"
        >
            <template #item="{ element: item, index }">
                <div class="EnumOptionsInput__row">
                    <EidIcon
                        :icon-src="faGripVertical"
                        alt="drag to reorder"
                        class="EnumOptionsInput__dragHandle"
                        :class="{ 'EnumOptionsInput__dragHandle--disabled': disabled || reorderSaving }"
                    />
                    <TextInput
                        :id="`EnumOptionsInput__option-${item.key}`"
                        :value="item.value"
                        placeholder="Enter option value"
                        :disabled="disabled || loadingUsage || getUsageCount(item.original) > 0"
                        @input="(e) => updateOption(index, e.target.value)"
                    />
                    <span
                        v-if="usageCounts !== null && getUsageCount(item.original) > 0"
                        class="EnumOptionsInput__usageCount"
                    >
                        {{ usageLabel(item.original) }}
                    </span>
                    <NewButton
                        class="EnumOptionsInput__removeButton"
                        variant="outline"
                        color="neutral"
                        :disabled="disabled || loadingUsage || getUsageCount(item.original) > 0"
                        @click="removeOption(index)"
                    >
                        <EidIcon :icon-src="faXmark" />
                    </NewButton>
                </div>
            </template>
        </draggable>

        <NewButton
            class="EnumOptionsInput__addButton"
            variant="outline"
            color="neutral"
            :disabled="disabled || loadingUsage || hasEmptyOption"
            @click="addOption"
        >
            + ADD OPTION
        </NewButton>

        <p v-if="showErrors && errorMessage" class="EnumOptionsInput__error">
            {{ errorMessage }}
        </p>
    </div>
</template>

<style lang="scss">
.EnumOptionsInput {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &__label {
        font-weight: 500;
        font-size: 14px;
    }

    &__required {
        color: var(--eidDangerRed);
    }

    &__list {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__dragHandle {
        cursor: grab;
        color: var(--eidTechGray);
        flex-shrink: 0;

        &--disabled {
            opacity: 0.4;
            cursor: not-allowed;
        }
    }

    &__row--ghost {
        opacity: 0.4;
    }

    &__usageCount {
        font-size: 12px;
        color: var(--eidTechGray);
        white-space: nowrap;
    }

    &__error {
        font-size: 12px;
        color: var(--eidDangerRed);
        margin: 0;
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import draggable from 'vuedraggable';

    import { faXmark, faGripVertical } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { EnumOptionUsage } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';
    import TextInput from '@/modules/dynamic-form/components/style-inputs/text-input/TextInput.vue';

    interface EnumOption {
        value: string;
        original: string;
        key: number;
    }

    export default defineComponent({
        name: 'EnumOptionsInput',

        components: { draggable, EidIcon, NewButton, TextInput },

        props: {
            value: {
                type: Array as PropType<string[]>,
                default: (): string[] => [],
            },
            usageCounts: {
                type: Object as PropType<Record<string, EnumOptionUsage | number> | null>,
                default: null,
            },
            loadingUsage: {
                type: Boolean,
                default: false,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            reorderSaving: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'input', 'reorder' ],

        data() {
            return {
                faXmark,
                faGripVertical,
                showErrors: false,
                items: this.value.map((v, i) => ({ value: v, original: v, key: i })) as EnumOption[],
                nextKey: this.value.length as number,
            };
        },

        computed: {
            hasEmptyOption(): boolean {
                return this.items.some((item) => item.value === '');
            },
            errorMessage(): string | null {
                const trimmed = this.items.map((item) => item.value.trim());
                if (trimmed.some((v) => v === '')) {
                    return 'Options cannot be empty';
                }
                if (new Set(trimmed).size !== trimmed.length) {
                    return 'Options must be unique';
                }
                return null;
            },
        },

        methods: {
            getUsageCount(original: string): number {
                const usage = this.usageCounts?.[original];
                if (typeof usage === 'number') {
                    return usage;
                }
                return usage ? usage.entities + usage.riskProfiles : 0;
            },
            usageLabel(original: string): string {
                const usage = this.usageCounts?.[original];
                if (!usage || typeof usage === 'number') {
                    return '';
                }
                const parts: string[] = [];
                if (usage.entities > 0) {
                    parts.push(`${usage.entities} ${usage.entities === 1 ? 'entity' : 'entities'}`);
                }
                if (usage.riskProfiles > 0) {
                    parts.push(`${usage.riskProfiles} risk ${usage.riskProfiles === 1 ? 'profile' : 'profiles'}`);
                }
                return parts.join(', ');
            },
            addOption() {
                this.items.push({ value: '', original: '', key: this.nextKey++ });
                this.$emit('input', this.items.map((item) => item.value));
                this.$nextTick(() => {
                    const inputs = (this.$el as HTMLElement).querySelectorAll<HTMLInputElement>('.TextInput input');
                    inputs[inputs.length - 1]?.focus();
                });
            },
            removeOption(index: number) {
                this.items.splice(index, 1);
                this.$emit('input', this.items.map((item) => item.value));
            },
            updateOption(index: number, newValue: string) {
                this.items[index].value = newValue;
                this.showErrors = true;
                this.$emit('input', this.items.map((item) => item.value));
            },
            validate(): boolean {
                this.showErrors = true;
                return this.errorMessage === null;
            },
            onDragEnd(event: { oldIndex: number, newIndex: number }) {
                if (event.oldIndex === event.newIndex) {
                    return;
                }
                const newOrder = this.items.map((item) => item.value);
                this.$emit('input', newOrder);
                this.$emit('reorder', newOrder);
            },
        },
    });
</script>
